/**
 * What a plan version is, relative to the plan of record — so the governance
 * list can say it instead of offering "Authorize plan of record" on every row.
 *
 * Until 2026-10-07 the list had two cases: the current plan of record, and
 * everything else with a green Authorize button — v0 that was never
 * authorized, v4 that was the plan of record for two weeks, and the version
 * committed ten minutes ago all looked the same (build contract §2.8).
 */

export type VersionRowState =
  /** The plan of record now. */
  | { kind: 'current' }
  /** Newer than the plan of record (or there is none yet): the one to authorize. */
  | { kind: 'candidate' }
  /** Was the plan of record once; superseded since. */
  | { kind: 'was_por'; authorizedAt: string; supersededAt: string | null }
  /** Older than the plan of record and never authorized. */
  | { kind: 'never_authorized' };

export interface PlanOfRecordRow {
  plan_version_id: string;
  status: string;
  authorized_at: string | null;
  superseded_at?: string | null;
}

export function versionRowState(
  version: { id: string; version_number: number },
  activePorVersionId: string | null,
  porRows: readonly PlanOfRecordRow[],
  versions: readonly { id: string; version_number: number }[],
): VersionRowState {
  if (activePorVersionId && version.id === activePorVersionId) return { kind: 'current' };

  const past = porRows.find((r) => r.plan_version_id === version.id && r.status !== 'active');
  if (past?.authorized_at) {
    return { kind: 'was_por', authorizedAt: past.authorized_at, supersededAt: past.superseded_at ?? null };
  }

  const porNumber = activePorVersionId
    ? versions.find((v) => v.id === activePorVersionId)?.version_number ?? null
    : null;
  if (porNumber == null || version.version_number > porNumber) return { kind: 'candidate' };
  return { kind: 'never_authorized' };
}

/** The words the row shows for a state that carries no button. */
export function versionRowCaption(state: VersionRowState, formatDate: (iso: string) => string): string | null {
  switch (state.kind) {
    case 'was_por':
      return state.supersededAt
        ? `Plan of record ${formatDate(state.authorizedAt)} – ${formatDate(state.supersededAt)}`
        : `Plan of record from ${formatDate(state.authorizedAt)}`;
    case 'never_authorized':
      return 'Never authorized';
    default:
      return null;
  }
}
